import type { U3204InitLU } from '../U3204Service';
import { u3202CapabilitySupported } from '../../u3202AiProvider/utils/u3202CapabilityHelpers';
import type { U3202ModelCapabilities } from '../../u3202AiProvider/utils/U3202CapabilitiesTypes';

export type U3204AttachmentValidationErrorCode =
  | 'too_many'
  | 'unsupported_mime'
  | 'file_too_large'
  | 'image_not_supported'
  | 'pdf_not_supported';

export interface U3204AttachmentValidationError {
  code: U3204AttachmentValidationErrorCode;
  fileName?: string;
}

const IMAGE_MIME_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const PDF_MIME_TYPE = 'application/pdf';

export function u3204FormatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

function u3204IsMimeAllowedByCapabilities(
  mime: string,
  capabilities?: U3202ModelCapabilities | null,
): boolean {
  if (!capabilities) {
    return true;
  }
  if (IMAGE_MIME_TYPES.includes(mime)) {
    return u3202CapabilitySupported(capabilities, 'image_input');
  }
  if (mime === PDF_MIME_TYPE) {
    return u3202CapabilitySupported(capabilities, 'pdf_input');
  }
  return true;
}

export function u3204ValidateAttachments(
  files: File[],
  initLU: Pick<U3204InitLU, 'allowedMimeTypes' | 'maxAttachmentBytes' | 'maxAttachmentsPerMessage'>,
  options?: {
    allowedMimeTypes?: string[];
    capabilities?: U3202ModelCapabilities | null;
  },
): U3204AttachmentValidationError | null {
  const effectiveAllowed = options?.allowedMimeTypes ?? initLU.allowedMimeTypes;

  if (files.length > initLU.maxAttachmentsPerMessage) {
    return { code: 'too_many' };
  }

  for (const file of files) {
    if (!effectiveAllowed.includes(file.type)) {
      if (IMAGE_MIME_TYPES.includes(file.type) && !u3204IsMimeAllowedByCapabilities(file.type, options?.capabilities)) {
        return { code: 'image_not_supported', fileName: file.name };
      }
      if (file.type === PDF_MIME_TYPE && !u3204IsMimeAllowedByCapabilities(file.type, options?.capabilities)) {
        return { code: 'pdf_not_supported', fileName: file.name };
      }
      return { code: 'unsupported_mime', fileName: file.name };
    }
    if (!u3204IsMimeAllowedByCapabilities(file.type, options?.capabilities)) {
      if (IMAGE_MIME_TYPES.includes(file.type)) {
        return { code: 'image_not_supported', fileName: file.name };
      }
      if (file.type === PDF_MIME_TYPE) {
        return { code: 'pdf_not_supported', fileName: file.name };
      }
    }
    if (file.size > initLU.maxAttachmentBytes) {
      return { code: 'file_too_large', fileName: file.name };
    }
  }

  return null;
}
